import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useBlocker, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  createPurchaseOrder,
  getPurchaseSuppliers,
  getSupplierPurchaseItems,
} from "../api/purchasesApi";
import OrderSupplierSelector from "../components/OrderSupplierSelector";
import EmptyOrderSuppliersDialog from "../components/EmptyOrderSuppliersDialog";
import PurchaseCardShell from "../components/PurchaseCardShell";
import PurchaseItemsToolbar from "../components/PurchaseItemsToolbar";
import PurchaseLinesTable from "../components/PurchaseLinesTable";
import RemoveOrderSupplierDialog from "../components/RemoveOrderSupplierDialog";
import UnsavedPurchaseDialog from "../components/UnsavedPurchaseDialog";
import { getPurchaseErrorMessage } from "../utils/purchaseError";
import {
  buildVisiblePurchaseItemRows,
  buildCreatePurchaseOrderPayload,
  createSupplierOrderDraft,
  normalizePurchaseSuppliers,
  normalizeSupplierPurchaseItem,
  supplierOrderDraftHasData,
  supplierPurchaseItemToDraft,
  validateCreatePurchaseOrder,
} from "../utils/purchaseModel";

const SEARCH_DELAY_MS = 300;
const EMPTY_ITEM_STATE = Object.freeze({
  searchInput: "",
  search: "",
  viewMode: "selected",
  availableItems: [],
  loading: false,
  error: "",
  reloadVersion: 0,
});

function createSupplierItemState() {
  return { ...EMPTY_ITEM_STATE };
}

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }
  return response;
}

export default function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const latestSuppliersRequestId = useRef(0);
  const latestItemsRequestId = useRef(0);
  const creatingRef = useRef(false);
  const navigationAllowedRef = useRef(false);
  const [supplierDrafts, setSupplierDrafts] = useState([]);
  const [activeSupplierId, setActiveSupplierId] = useState(null);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierInput, setSupplierInput] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [suppliersError, setSuppliersError] = useState("");
  const [suppliersReloadVersion, setSuppliersReloadVersion] = useState(0);
  const [supplierPendingRemoval, setSupplierPendingRemoval] = useState(null);
  const [supplierItemStates, setSupplierItemStates] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [createError, setCreateError] = useState("");
  const [creating, setCreating] = useState(false);
  const [emptySupplierDrafts, setEmptySupplierDrafts] = useState([]);
  const [draftsPendingCreation, setDraftsPendingCreation] = useState([]);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const dirty = supplierDrafts.length > 0;
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      !navigationAllowedRef.current &&
      `${currentLocation.pathname}${currentLocation.search}` !==
        `${nextLocation.pathname}${nextLocation.search}`
  );

  useEffect(() => {
    if (navigationBlocker.state === "blocked") setDiscardDialogOpen(true);
  }, [navigationBlocker.state]);

  useEffect(() => {
    if (!dirty || navigationAllowedRef.current) return undefined;
    const handleBeforeUnload = (event) => {
      if (navigationAllowedRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    const timeout = setTimeout(
      () => setSupplierSearch(supplierInput.trim()),
      SEARCH_DELAY_MS
    );
    return () => clearTimeout(timeout);
  }, [supplierInput]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = ++latestSuppliersRequestId.current;

    async function fetchSuppliers() {
      setSuppliersLoading(true);
      setSuppliersError("");
      try {
        const response = await getPurchaseSuppliers({
          limit: 100,
          search: supplierSearch || undefined,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          requestId !== latestSuppliersRequestId.current
        ) {
          return;
        }
        setSupplierOptions(normalizePurchaseSuppliers(unwrapHttp(response)));
      } catch (error) {
        if (
          controller.signal.aborted ||
          requestId !== latestSuppliersRequestId.current
        ) {
          return;
        }
        setSupplierOptions([]);
        setSuppliersError(
          getPurchaseErrorMessage(error, "No se pudieron cargar los proveedores.")
        );
      } finally {
        if (
          !controller.signal.aborted &&
          requestId === latestSuppliersRequestId.current
        ) {
          setSuppliersLoading(false);
        }
      }
    }

    fetchSuppliers();
    return () => controller.abort();
  }, [supplierSearch, suppliersReloadVersion]);

  const activeItemState =
    supplierItemStates[activeSupplierId] || EMPTY_ITEM_STATE;
  const activeItemSearchInput = activeItemState.searchInput;
  const activeItemSearch = activeItemState.search;
  const activeItemsReloadVersion = activeItemState.reloadVersion;

  useEffect(() => {
    if (!activeSupplierId) return undefined;
    const supplierId = activeSupplierId;
    const timeout = setTimeout(() => {
      setSupplierItemStates((current) => ({
        ...current,
        [supplierId]: {
          ...(current[supplierId] || createSupplierItemState()),
          search: activeItemSearchInput.trim(),
        },
      }));
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [activeItemSearchInput, activeSupplierId]);

  useEffect(() => {
    if (!activeSupplierId) return undefined;
    const supplierId = activeSupplierId;
    const controller = new AbortController();
    const requestId = ++latestItemsRequestId.current;

    setSupplierItemStates((current) => ({
      ...current,
      [supplierId]: {
        ...(current[supplierId] || createSupplierItemState()),
        loading: true,
        error: "",
      },
    }));

    async function fetchItems() {
      try {
        const response = await getSupplierPurchaseItems(supplierId, {
          search: activeItemSearch || undefined,
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          requestId !== latestItemsRequestId.current
        ) {
          return;
        }
        const payload = unwrapHttp(response);
        const availableItems = (Array.isArray(payload) ? payload : []).map(
          normalizeSupplierPurchaseItem
        );
        setSupplierItemStates((current) => ({
          ...current,
          [supplierId]: {
            ...(current[supplierId] || createSupplierItemState()),
            availableItems,
            loading: false,
            error: "",
          },
        }));
      } catch (error) {
        if (
          controller.signal.aborted ||
          requestId !== latestItemsRequestId.current
        ) {
          return;
        }
        setSupplierItemStates((current) => ({
          ...current,
          [supplierId]: {
            ...(current[supplierId] || createSupplierItemState()),
            availableItems: [],
            loading: false,
            error: getPurchaseErrorMessage(
              error,
              "No se pudieron cargar los ítems del proveedor."
            ),
          },
        }));
      }
    }

    fetchItems();
    return () => controller.abort();
  }, [activeItemSearch, activeItemsReloadVersion, activeSupplierId]);

  const availableSupplierOptions = useMemo(() => {
    const selectedIds = new Set(
      supplierDrafts.map((draft) => Number(draft.supplierId))
    );
    return supplierOptions.filter(
      (supplier) => !selectedIds.has(Number(supplier.id))
    );
  }, [supplierDrafts, supplierOptions]);

  const activeSupplier = supplierDrafts.find(
    (draft) => Number(draft.supplierId) === Number(activeSupplierId)
  );
  const selectedItemIds = useMemo(
    () =>
      new Set(
        (activeSupplier?.lines || []).map((line) =>
          Number(line.purchaseItemId)
        )
      ),
    [activeSupplier?.lines]
  );
  const visibleRows = useMemo(
    () =>
      buildVisiblePurchaseItemRows({
        lines: activeSupplier?.lines || [],
        availableItems: activeItemState.availableItems,
        viewMode: activeItemState.viewMode,
        filtersActive: Boolean(activeItemState.search),
      }),
    [
      activeItemState.availableItems,
      activeItemState.search,
      activeItemState.viewMode,
      activeSupplier?.lines,
    ]
  );

  function updateSupplierItemState(supplierId, changes) {
    setSupplierItemStates((current) => ({
      ...current,
      [supplierId]: {
        ...(current[supplierId] || createSupplierItemState()),
        ...changes,
      },
    }));
  }

  function updateActiveSupplierLines(updater) {
    if (!activeSupplierId || creating) return;
    setSupplierDrafts((current) =>
      current.map((draft) =>
        Number(draft.supplierId) === Number(activeSupplierId)
          ? { ...draft, lines: updater(draft.lines) }
          : draft
      )
    );
  }

  function clearSupplierValidation(supplierId) {
    setValidationErrors((current) => {
      if (!current[supplierId]) return current;
      const next = { ...current };
      delete next[supplierId];
      return next;
    });
    setFormError("");
    setCreateError("");
  }

  function addSupplier(supplier) {
    if (creating) return;
    if (
      supplierDrafts.some(
        (draft) => Number(draft.supplierId) === Number(supplier.id)
      )
    ) {
      return;
    }
    setSupplierDrafts((current) => [
      ...current,
      createSupplierOrderDraft(supplier),
    ]);
    setSupplierItemStates((current) => ({
      ...current,
      [supplier.id]: createSupplierItemState(),
    }));
    setActiveSupplierId(Number(supplier.id));
    setSupplierInput("");
    setFormError("");
    setCreateError("");
  }

  function removeSupplier(supplierId) {
    if (creating) return;
    setSupplierDrafts((current) => {
      const remaining = current.filter(
        (draft) => Number(draft.supplierId) !== Number(supplierId)
      );
      if (Number(activeSupplierId) === Number(supplierId)) {
        setActiveSupplierId(remaining[0]?.supplierId ?? null);
      }
      return remaining;
    });
    setSupplierItemStates((current) => {
      const next = { ...current };
      delete next[supplierId];
      return next;
    });
    setSupplierPendingRemoval(null);
    clearSupplierValidation(supplierId);
  }

  function requestSupplierRemoval(supplierId) {
    const draft = supplierDrafts.find(
      (candidate) => Number(candidate.supplierId) === Number(supplierId)
    );
    if (!draft) return;
    if (supplierOrderDraftHasData(draft)) {
      setSupplierPendingRemoval(draft);
      return;
    }
    removeSupplier(supplierId);
  }

  function handleItemSearchChange(value) {
    updateSupplierItemState(activeSupplierId, {
      searchInput: value,
      viewMode: value.trim() ? "all" : activeItemState.viewMode,
    });
  }

  function handleAddItem(line) {
    if (creating || selectedItemIds.has(Number(line.purchaseItemId))) return;
    updateActiveSupplierLines((current) => [
      ...current,
      supplierPurchaseItemToDraft(line),
    ]);
    clearSupplierValidation(activeSupplierId);
  }

  function handleRemoveItem(purchaseItemId) {
    updateActiveSupplierLines((current) =>
      current.filter(
        (line) => Number(line.purchaseItemId) !== Number(purchaseItemId)
      )
    );
    clearSupplierValidation(activeSupplierId);
  }

  function handleFieldChange(purchaseItemId, field, value) {
    updateActiveSupplierLines((current) =>
      current.map((line) =>
        Number(line.purchaseItemId) === Number(purchaseItemId)
          ? { ...line, [field]: value }
          : line
      )
    );
    setValidationErrors((current) => {
      const supplierErrors = current[activeSupplierId];
      if (!supplierErrors?.[purchaseItemId]?.[field]) return current;
      const lineErrors = { ...supplierErrors[purchaseItemId] };
      delete lineErrors[field];
      const nextSupplierErrors = { ...supplierErrors };
      if (Object.keys(lineErrors).length) {
        nextSupplierErrors[purchaseItemId] = lineErrors;
      } else {
        delete nextSupplierErrors[purchaseItemId];
      }
      const next = { ...current };
      if (Object.keys(nextSupplierErrors).length) {
        next[activeSupplierId] = nextSupplierErrors;
      } else {
        delete next[activeSupplierId];
      }
      return next;
    });
    setFormError("");
    setCreateError("");
  }

  async function submitPurchaseOrder(validDrafts) {
    if (creatingRef.current) return;
    creatingRef.current = true;
    setCreating(true);
    setCreateError("");
    try {
      await createPurchaseOrder(buildCreatePurchaseOrderPayload(validDrafts));
      navigationAllowedRef.current = true;
      navigate("/stock/operacion/compras", {
        replace: true,
        state: { successMessage: "Pedido creado correctamente." },
      });
    } catch (error) {
      setCreateError(
        getPurchaseErrorMessage(
          error,
          "No se pudo crear el pedido. Revisá los datos e intentá nuevamente."
        )
      );
    } finally {
      creatingRef.current = false;
      setCreating(false);
    }
  }

  function requestCreatePurchaseOrder() {
    if (creatingRef.current) return;
    const result = validateCreatePurchaseOrder(supplierDrafts);
    setValidationErrors(result.supplierErrors);
    setFormError(result.formError);
    setCreateError("");

    const invalidSupplierId = Object.keys(result.supplierErrors)[0];
    if (invalidSupplierId) {
      setActiveSupplierId(Number(invalidSupplierId));
      return;
    }
    if (result.formError) return;

    if (result.emptyDrafts.length) {
      setEmptySupplierDrafts(result.emptyDrafts);
      setDraftsPendingCreation(result.validDrafts);
      return;
    }
    submitPurchaseOrder(result.validDrafts);
  }

  function continueWithoutEmptySuppliers() {
    const validDrafts = draftsPendingCreation;
    setEmptySupplierDrafts([]);
    setDraftsPendingCreation([]);
    submitPurchaseOrder(validDrafts);
  }

  function requestCancel() {
    if (creating) return;
    if (dirty) {
      setDiscardDialogOpen(true);
      return;
    }
    navigate(-1);
  }

  function keepEditing() {
    setDiscardDialogOpen(false);
    if (navigationBlocker.state === "blocked") navigationBlocker.reset();
  }

  function discardChanges() {
    setDiscardDialogOpen(false);
    navigationAllowedRef.current = true;
    if (navigationBlocker.state === "blocked") {
      navigationBlocker.proceed();
      return;
    }
    navigate(-1);
  }

  const emptyItemsMessage =
    activeItemState.viewMode === "selected"
      ? "Todavía no seleccionaste ítems para este proveedor."
      : activeItemState.search
      ? "No se encontraron ítems con esa búsqueda."
      : "Este proveedor no tiene ítems disponibles.";

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        width: "100%",
        minHeight: 0,
        minWidth: 0,
        boxSizing: "border-box",
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader title="Pedido" onBack={requestCancel} />

      <PurchaseCardShell
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: "neutral.50",
          borderRadius: "24px",
          boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.22)",
          minWidth: 0,
        }}
      >
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          {formError || createError ? (
            <Alert
              severity="error"
              onClose={createError ? () => setCreateError("") : undefined}
              sx={{ borderRadius: "14px", fontWeight: 650 }}
            >
              {createError || formError}
            </Alert>
          ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(220px, 280px) minmax(0, 1fr)",
            },
            flex: 1,
            minHeight: { xs: 420, md: 0 },
            overflow: { md: "hidden" },
          }}
        >
          <Stack
            sx={{
              pr: { md: 2 },
              pb: { xs: 2.5, md: 0 },
              minHeight: 0,
              borderRight: { md: "1px solid" },
              borderBottom: { xs: "1px solid", md: 0 },
              borderColor: "neutral.600",
            }}
          >
            <OrderSupplierSelector
              options={availableSupplierOptions}
              drafts={supplierDrafts}
              activeSupplierId={activeSupplierId}
              inputValue={supplierInput}
              loading={suppliersLoading}
              error={suppliersError}
              disabled={creating}
              onInputChange={setSupplierInput}
              onSelect={addSupplier}
              onActivate={setActiveSupplierId}
              onRemove={requestSupplierRemoval}
              onRetry={() => setSuppliersReloadVersion((current) => current + 1)}
            />
          </Stack>

          <Stack
            spacing={1.25}
            sx={{
              pl: { md: 2 },
              pt: { xs: 2.5, md: 0 },
              minWidth: 0,
              minHeight: 0,
              overflow: { md: "hidden" },
            }}
          >
            {activeSupplier ? (
              <>
                <PurchaseItemsToolbar
                  search={activeItemState.searchInput}
                  viewMode={activeItemState.viewMode}
                  showCategory={false}
                  searchPlaceholder="Seleccionar ítem"
                  searchAriaLabel={`Buscar ítem de ${activeSupplier.supplierName}`}
                  disabled={creating}
                  onSearchChange={handleItemSearchChange}
                  onViewModeChange={(viewMode) =>
                    updateSupplierItemState(activeSupplierId, { viewMode })
                  }
                />

                <Typography sx={{ color: "neutral.700", fontSize: 12, fontWeight: 750 }}>
                  Ítems de {activeSupplier.supplierName}
                </Typography>

                {activeItemState.error ? (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                          updateSupplierItemState(activeSupplierId, {
                            reloadVersion: activeItemState.reloadVersion + 1,
                          })
                        }
                      >
                        Reintentar
                      </Button>
                    }
                    sx={{ borderRadius: "14px", fontWeight: 650 }}
                  >
                    {activeItemState.error}
                  </Alert>
                ) : null}

                <PurchaseLinesTable
                  rows={visibleRows}
                  loading={activeItemState.loading}
                  readOnly={creating}
                  errors={validationErrors[activeSupplierId] || {}}
                  showTotals={false}
                  onFieldChange={handleFieldChange}
                  onAdd={handleAddItem}
                  onRemove={handleRemoveItem}
                  emptyMessage={emptyItemsMessage}
                />
              </>
            ) : (
              <>
                <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 750 }}>
                  Seleccionar ítem
                </Typography>
                <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ color: "neutral.600", fontSize: 14, fontWeight: 700 }}>
                    Seleccioná un proveedor para consultar sus ítems.
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Box>

          <Stack
            direction="row"
            spacing={1.25}
            sx={{ justifyContent: "flex-end", flexShrink: 0 }}
          >
            <Button
              type="button"
              variant="contained"
              onClick={requestCancel}
              disabled={creating}
              sx={{
                minWidth: 120,
                borderRadius: "33px",
                bgcolor: "secondary.50",
                color: "primary.900",
                textTransform: "none",
                fontWeight: 800,
                "&:hover": { bgcolor: "secondary.100" },
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={requestCreatePurchaseOrder}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                minWidth: 150,
                borderRadius: "33px",
                textTransform: "none",
                fontWeight: 800,
              }}
            >
              {creating ? "Creando..." : "Crear pedido"}
            </Button>
          </Stack>
        </Stack>
      </PurchaseCardShell>

      <RemoveOrderSupplierDialog
        open={Boolean(supplierPendingRemoval)}
        supplierName={supplierPendingRemoval?.supplierName || "este proveedor"}
        onCancel={() => setSupplierPendingRemoval(null)}
        onConfirm={() => removeSupplier(supplierPendingRemoval?.supplierId)}
      />

      <EmptyOrderSuppliersDialog
        open={Boolean(emptySupplierDrafts.length)}
        supplierNames={emptySupplierDrafts.map((draft) => draft.supplierName)}
        disabled={creating}
        onCancel={() => {
          setEmptySupplierDrafts([]);
          setDraftsPendingCreation([]);
        }}
        onContinue={continueWithoutEmptySuppliers}
      />

      <UnsavedPurchaseDialog
        open={discardDialogOpen}
        disabled={creating}
        message="Si salís ahora, los proveedores, ítems y valores cargados en el pedido se perderán."
        onKeepEditing={keepEditing}
        onDiscard={discardChanges}
      />
    </Stack>
  );
}
